import { useState, useEffect, useRef, MouseEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";

// Toolkit
import { doUserEnrollment } from "sj-ui-toolkit/src/api";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import {
  assessmentUpdate,
  getContentClient,
  getScormPackageClient,
  SandboxLessonDetails,
} from "sj-ui-toolkit/src/client";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";
import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useTrackTime } from "sj-ui-toolkit/src/hooks/useTrackTime";
import { exitScormLesson, setScormPassed } from "sj-ui-toolkit/src/utils/rxd";

// Context providers
import { useLearnContext } from "../../context/LearnContext";

// Hooks
import { useLessonLoader } from "../../hooks/useLessonLoader";
import { usePathCompletion } from "../../hooks/usePathCompletion";
import { useToast, useSegmentTrackEvent } from "sj-ui-toolkit/src/hooks";

// Sub-components
import AppLabLesson from "./AppLabLesson/AppLabLesson";
import LessonCoverPage from "./LessonCoverPage";
import { PathCompletionPage } from "./PathCompletion/PathCompletionPage";
import SandboxLesson from "./SandboxLesson";
import VideoLesson from "./VideoLesson";
import { VideoLessonDetails } from "./VideoLesson/VideoProvider";
import { CombinedFeedback } from "./Survey/Survey";

// Main component
export default function LessonPage() {
  const { lessonKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isScorm, isGuest, isAssignment } = useLearnContext();
  const { selectedTenant } = useUserContext();
  const { showToast } = useToast();

  const mainRef = useRef<HTMLDivElement>(null);

  // Path context passed along from the Path/Quest page; roleEnrollmentId is only present when enrolled
  const roleId = searchParams.get("roleId");
  const roleEnrollmentId = searchParams.get("roleEnrollmentId");

  const { lessonDetails, setLessonDetails } = useLessonLoader({
    lessonKey: lessonKey ?? null,
    roleId: roleId?.trim() ? roleId : undefined,
  });

  const nextLessonKey = lessonDetails?.nextLesson?.lessonKey;
  const hasNextLesson = !!roleId && !!nextLessonKey;

  // Register this page's breadcrumb — trail is auto-accumulated by the provider
  useBreadcrumb(
    lessonDetails ? { label: lessonDetails.title ?? "", route: `/lesson/${lessonKey}` } : null
  );

  // Track time spent on the lesson
  useTrackTime({ lessonId: lessonDetails?.id });

  // Send Segment event when the lesson page is visited
  const trackEvent = useSegmentTrackEvent();
  const hasTrackedLaunch = useRef(false);
  const lessonCompletedRef = useRef(false);
  const launchTimeRef = useRef<number | null>(null);
  const trackEventRef = useRef(trackEvent);
  const lessonKeyRef = useRef(lessonKey);
  const segmentContext = isAssignment ? { context: "assignment" } : {};
  const segmentContextRef = useRef(segmentContext);
  trackEventRef.current = trackEvent;
  lessonKeyRef.current = lessonKey;
  segmentContextRef.current = segmentContext;

  // Reset tracking refs when navigating to a different lesson within the same component instance
  useEffect(() => {
    hasTrackedLaunch.current = false;
    lessonCompletedRef.current = false;
    launchTimeRef.current = null;
  }, [lessonKey]);

  useEffect(() => {
    if (lessonDetails && !hasTrackedLaunch.current) {
      trackEvent("Lesson launch", { lessonKey, ...segmentContext });
      hasTrackedLaunch.current = true;
      launchTimeRef.current = Date.now();
      if (isScorm) {
        getScormPackageClient().recordLessonAttemptStarted({
          lessonKey: lessonKey ?? "",
          tenant: selectedTenant ?? "",
        });
      }
    }
  }, [lessonDetails, lessonKey, trackEvent]);

  // Send bounce/abandoned event on unmount
  useEffect(() => {
    return () => {
      if (!launchTimeRef.current || lessonCompletedRef.current) return;
      const elapsed = Date.now() - launchTimeRef.current;
      const event = elapsed < 30000 ? "Lesson bounce" : "Lesson abandoned";
      trackEventRef.current(event, {
        lessonKey: lessonKeyRef.current,
        ...segmentContextRef.current,
      });
    };
  }, []);

  // Subscribe to PathCompletedChannel — silently stores data until lesson finishes
  const { isPathCompleted, completedPath } = usePathCompletion();

  // Track whether to show the path completion screen (after survey, before navigation)
  const [showPathCompletion, setShowPathCompletion] = useState(false);

  // Update page title for accessibility
  useEffect(() => {
    const previousPageTitle = document.title;

    if (lessonDetails?.title) {
      document.title = lessonDetails.title;
    }

    return () => {
      document.title = previousPageTitle;
    };
  }, [lessonDetails?.title]);

  // Reposition screen reader focus on view transition
  useEffect(() => {
    mainRef.current?.focus();
  }, [lessonDetails?.showBeforeSurvey, lessonDetails?.showAfterSurvey, showPathCompletion]);

  // Navigate to the next lesson in the path, carrying the path context along
  const goToNextLesson = (enrollmentId?: number | string) => {
    navigate(
      buildRoute(
        "Learn.routes.lesson.route",
        { lessonKey: nextLessonKey ?? "" },
        {
          roleId: roleId ?? undefined,
          ...(enrollmentId && { roleEnrollmentId: String(enrollmentId) }),
        }
      )
    );
  };

  // Prompt an unenrolled user to enroll in the path before continuing to the next lesson
  const confirmEnrollment = (target?: HTMLElement) => {
    confirmPopup({
      target,
      message:
        "You're not enrolled in this path. Would you like to enroll and receive a certificate for your completed lessons?",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Enroll",
      rejectLabel: "Not now",
      accept: async () => {
        try {
          const response = await doUserEnrollment(Number(roleId));
          showToast({
            severity: "success",
            summary: "Enrolled",
            detail: "You have successfully enrolled in this path.",
            life: 3000,
          });
          goToNextLesson(response?.roleEnrollment?.id);
        } catch (_error) {
          showToast({
            severity: "error",
            summary: "Error",
            detail: "Failed to enroll in path. Please try again.",
            life: 3000,
          });
        }
      },
      reject: () => goToNextLesson(),
      style: { maxWidth: "400px" },
      pt: {
        root: { role: "alertdialog", "aria-modal": "true", "aria-label": "Enroll in path" },
        content: { role: "region", "aria-label": "Enroll in path" },
      },
    });
  };

  const submitLessonFeedback = async (
    feedback: CombinedFeedback,
    isExiting = false,
    event?: MouseEvent<HTMLButtonElement>
  ) => {
    const contentClient = getContentClient();
    if ((feedback.feedbackText || feedback.rating) && selectedTenant) {
      try {
        const contentKey = lessonDetails?.experiment?.stiltsImage as string | undefined;
        await contentClient.submitContentFeedback({
          ...feedback,
          selectedTenant,
          lessonKey,
          contentKey,
        });
      } catch (error) {
        console.error("Error submitting feedback:", error);
        showToast({
          severity: "error",
          summary: "Feedback submission failed",
          detail: "There was an issue submitting your feedback. Please try again.",
          life: 5000,
        });
      }
    }

    await submitKnowledgeRating(feedback.knowledgeRating, isExiting, event);
  };

  // Define handlers for user actions
  const submitKnowledgeRating = async (
    rating: string | null,
    isExiting = false,
    event?: MouseEvent<HTMLButtonElement>
  ) => {
    if (!lessonDetails) return;
    // Capture the button before any await — React clears event.currentTarget after the handler
    const enrollPopupTarget = event?.currentTarget;
    const isAfterPage = lessonDetails.showAfterSurvey;

    if (rating && lessonDetails.assessment) {
      // Keys are camelCase; the Rails client decamelizes them to snake_case on the request.
      const params: { afterRating?: number; beforeRating?: number } = {};
      if (isAfterPage) {
        params.afterRating = parseInt(rating, 10);
      } else {
        params.beforeRating = parseInt(rating, 10);
      }

      try {
        await assessmentUpdate({
          id: lessonDetails.assessment.id,
          paramsToUpdate: params,
          useToast: false,
        });
      } catch (error) {
        console.error("Error submitting knowledge rating:", error);
        showToast({
          severity: "error",
          summary: "Submission failed",
          detail: "There was an issue submitting your feedback. Please try again.",
          life: 5000,
        });
      }
    }

    // Proceed based on whether it was before or after survey
    if (isAfterPage) {
      // If a path was completed, show the path completion screen before navigating
      if (isPathCompleted && completedPath) {
        setShowPathCompletion(true);
        return;
      }

      if (isExiting) {
        navigate("/");
        return;
      }
      // Navigate to the next lesson if we're in a path context and nextLesson exists
      if (hasNextLesson) {
        if (roleEnrollmentId) {
          goToNextLesson(roleEnrollmentId);
        } else {
          confirmEnrollment(enrollPopupTarget);
        }
      } else {
        navigate("/");
      }
    } else {
      // Hide the before survey and start the lesson
      trackEvent("Lesson before survey completed", { lessonKey, ...segmentContext });
      setLessonDetails({ ...lessonDetails, showBeforeSurvey: false });
    }
  };

  const onLessonFinish = () => {
    if (lessonCompletedRef.current) return;
    lessonCompletedRef.current = true;
    trackEvent("Lesson completed", { lessonKey, ...segmentContext });
    if (!isGuest && !isScorm) {
      setLessonDetails({ ...lessonDetails, showAfterSurvey: true });
    } else {
      // Set SCORM as passed and exit the lesson for guest users and SCORM content since they won't see the after lesson survey
      if (isScorm) {
        getScormPackageClient().recordLessonAttemptCompleted({
          lessonKey: lessonKey ?? "",
          tenant: selectedTenant ?? "",
        });
      }
      setScormPassed({ score: 100, max: 100, min: 0 });
      exitScormLesson();
    }
  };

  // Return the full screen loader until we have lesson details
  if (!lessonDetails) {
    return (
      <div className="h-full">
        <FullScreenLoader />
      </div>
    );
  }

  // Show path completion screen after the survey if a path was completed
  if (showPathCompletion && completedPath) {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <PathCompletionPage completedPath={completedPath} />
      </div>
    );
  }

  // Render the After Lesson Feedback page if needed
  if (lessonDetails.showAfterSurvey) {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <ConfirmPopup />
        <LessonCoverPage
          headerText="Lesson complete!"
          description={lessonDetails.description}
          lessonType={lessonDetails.type}
          title={lessonDetails.title}
          level={lessonDetails.level}
          duration={lessonDetails.duration}
          isAfterLessonSurvey={true}
          onSubmit={submitLessonFeedback}
          hasNextLesson={hasNextLesson}
          isPathCompleted={isPathCompleted && completedPath !== null}
        />
      </div>
    );
  }

  // Render the Getting Started screen/survey if needed
  if (lessonDetails.showBeforeSurvey && !isGuest && !isScorm) {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <LessonCoverPage
          headerText="Let's get started."
          description={lessonDetails.description}
          lessonType={lessonDetails.type}
          title={lessonDetails.title}
          difficulty={lessonDetails.difficulty}
          duration={lessonDetails.duration}
          isAfterLessonSurvey={false}
          onSubmit={submitLessonFeedback}
        />
      </div>
    );
  }

  // Render the appropriate lesson type. useLessonLoader returns a loose aggregate; the runtime
  // object matches each lesson-type's specific shape. Route through `unknown` once so every cast
  // below is a single, consistent assertion. TODO(T13): type the loader per lesson type to drop these.
  const details = lessonDetails as unknown;
  if (lessonDetails.isBreakFix) {
    if (lessonDetails.lessonType === "AppLab") {
      return (
        <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
          <AppLabLesson
            lessonDetails={details as SandboxLessonDetails}
            onLessonFinish={onLessonFinish}
          />
        </div>
      );
    } else {
      return (
        <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
          <SandboxLesson
            lessonDetails={details as SandboxLessonDetails}
            onLessonFinish={onLessonFinish}
          />
        </div>
      );
    }
  } else {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <VideoLesson
          lessonDetails={details as VideoLessonDetails}
          onLessonFinish={onLessonFinish}
        />
      </div>
    );
  }
}
